import { Text, FormGroup, Input, Progress, Icons } from '@agro-club/agroclub-shared'
import { useAFormHandler } from 'analytics/hooks'
import { useFormik } from 'formik'
import { endpoints } from 'modules/endpoints'
import { apiClient } from 'modules/utils/httpClient'
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { SharedModalContent, SharedModalFooter, SharedModalInputs } from 'views/components/SharedModal'
import { SampleDetailed, SampleWithRequests } from 'views/pages/Samples/types'
import { ShortFormControl } from 'views/styled/common'
import { AButton } from 'views/components/Analytics'
import { PublishPreview } from '../PublishPreview'
import { DocumentFile, RespFile } from 'modules/domain/types'
import { FilesControl } from '../FIlesControl/FilesControl'
import { QuantityInput } from 'views/components/QuantityInput/QuantityInput'
import { HrWithHeader } from 'views/components/Hr/Hr'
import { SampleDataForPublishingBot, SampleParam } from '../../../types'

type Props = {
  data: SampleDataForPublishingBot
  docs: DocumentFile[] | RespFile[]
  sample: SampleDetailed | SampleWithRequests
  onSuccess: () => Promise<void>
  filesProgress?: Progress
  addFileHandler?: (files: FileList) => unknown
  onClose: () => void
  children?: React.ReactNode
  selectAllFiles?: boolean
}

type Fields = {
  parameters: SampleParam[]
  quantity: number
  documents: number[]
}

export const PublishLocalForm: FC<Props> = ({
  onClose,
  onSuccess,
  data,
  docs,
  sample,
  filesProgress,
  addFileHandler,
  children,
  selectAllFiles,
}) => {
  const { t } = useTranslation('samples')
  const { formProgress, formHandler } = useAFormHandler('editSample', { id: sample.id.toString() })
  const [previewContent, setPreviewContent] = useState('')
  const [previewDocuments, setPreviewDocuments] = useState<DocumentFile[]>([])

  const formik = useFormik<Fields>({
    initialValues: {
      parameters: data.parameters || [],
      documents: selectAllFiles ? docs.map((doc) => doc.id) : [],
      quantity: data.quantity,
    },
    //onSumbit unused here
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onSubmit: () => {},
  })

  const customSubmit = (isPreview: boolean) =>
    formHandler(
      async () => {
        const dto = {
          parameters: formik.values.parameters
            //.filter((param) => param.value)
            .map((param) => ({
              parameter: param.parameter.id,
              value: param.value,
            })),
          selected_files: formik.values.documents,
          quantity: formik.values.quantity,
        }
        return await apiClient.put(
          isPreview
            ? endpoints.samplesForLocalBotPreview(sample.id.toString())
            : endpoints.samplesForLocalBotSend(sample.id.toString()),
          dto,
        )
      },
      {
        onSuccess: async (response) => {
          if (isPreview) {
            setPreviewContent(response.message_text)
            setPreviewDocuments(response.selected_files)
            return
          }
          await onSuccess()
          onClose()
        },
        onError: (error) => {
          const flatErrors = error.errors
          if (flatErrors.parameters) {
            flatErrors.parameters = flatErrors.parameters.map((p: any) => p.parameter || p.value)
          }
          formik.setErrors(flatErrors)
        },
      },
    )()

  const onDocClick = (docId: number) => {
    if (formik.values.documents.find((selected) => selected.toString() === docId.toString())) {
      formik.setFieldValue(
        'documents',
        formik.values.documents.filter((selected) => selected.toString() !== docId.toString()),
      )
    } else {
      formik.setFieldValue('documents', [...formik.values.documents, docId])
    }
  }

  return (
    <>
      <SharedModalContent>
        {children}
        <SharedModalInputs>
          <Text color="secondary" typography="bodyLarge">
            {t('bidNumber')}
            {data.bid_id}
          </Text>
          <ShortFormControl>
            <FormGroup label={t('season')} style={{ alignItems: 'center' }}>
              <div>{data.season.name}</div>
            </FormGroup>
            <QuantityInput formik={formik} fieldName="quantity" label={t('common:volume')} required />
            {formik.values.parameters.map((param, index) => (
              <FormGroup
                key={param.parameter.id}
                label={[param.parameter.name, param.parameter.units].filter(Boolean).join(', ')}
                error={formik.errors?.parameters?.[index]}
                required
                afterControlComponent={
                  <div style={{ marginLeft: '8px' }}>
                    <AButton
                      Icon={Icons.IconDelete}
                      color="secondary"
                      background="none"
                      size="big"
                      id="deleteParam"
                      onClick={() =>
                        formik.setFieldValue(
                          'parameters',
                          formik.values.parameters.filter((_, i) => i !== index),
                        )
                      }
                    />
                  </div>
                }
              >
                <Input
                  value={param.value}
                  type="number"
                  onChange={(e) => formik.setFieldValue(`parameters[${index}]`, { ...param, value: e.target.value })}
                  invalid={!!formik.errors?.parameters?.[index]}
                />
              </FormGroup>
            ))}
          </ShortFormControl>
          <HrWithHeader text={t('common:files')} />
          <FilesControl
            docs={docs}
            selectedFiles={formik.values.documents}
            onUpload={addFileHandler}
            onDocClick={onDocClick}
            uploadProgress={filesProgress}
          />
        </SharedModalInputs>
      </SharedModalContent>
      <SharedModalFooter>
        <AButton id="send" size="big" progress={formProgress} color="primary" onClick={() => customSubmit(false)}>
          {t('common:send')}
        </AButton>
        <AButton id="send" size="big" progress={formProgress} color="secondary" onClick={() => customSubmit(true)}>
          {t('preview')}
        </AButton>
        <AButton id="cancel" size="big" onClick={onClose} color="secondary">
          {t('common:cancel')}
        </AButton>
      </SharedModalFooter>
      {previewContent && (
        <PublishPreview
          onClose={() => {
            setPreviewContent('')
            setPreviewDocuments([])
          }}
          sendMessage={() => customSubmit(false)}
          documents={previewDocuments}
          html={previewContent}
        />
      )}
    </>
  )
}
